import _ from "lodash";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import RadialProgressBar from "UIComponents/RadialProgressBar";
import I18nHOC from "UIComponents/I18nHOC";
import withRecorder from "../WithRecorder";
import { compose } from "lib/apolloClient";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { connect } from "react-redux";
import { colors } from "Constants";
import {
  MicrophoneOutlined,
  TickLargeOutlined,
  PauseFilled,
  PlayFilled,
} from "@toddle-edu/ds-icons";
/**
 * updateAudioRecorderBlob will be used to save recording blob in redux as draft
 */
import { updateAudioRecorderBlob } from "modules/Services";

import classes from "./AudioRecorderV3.scss";
import classNames from "classnames";
import { Modal, Button, Dialog } from "@toddle-edu/ds-web";

const DIALOGS_INFO = {
  DISCARD: {
    title: "common:label_with_recording",
    label: "common:discard",
    message: "common:recording_modal_warning",
    discardButtonText: "common:discard",
    cancelButtonText: "common:cancel",
  },
};

const AudioRecorderV3 = forwardRef((props, forwardedRef) => {
  const {
    t,
    timer,
    calculatedPercent,
    cancelRecording,
    startRecording,
    stopRecording,
    togglePause,
    playerState,
    isPaused,
    timeStamp,
    headerLabel,
    onDraftModalOutsideClick,
    saveDraftRecording,
  } = props;

  const [modalStateOpen, setModalStateOpen] = useState(true);
  const [currentDialogAction, setCurrentDialogAction] = useState("");

  const handleClose = () => {
    setModalStateOpen(false);
    setCurrentDialogAction("");
    onDraftModalOutsideClick();
  };

  const handleCloseModal = () => {
    if (timeStamp > 0 && !saveDraftRecording) {
      togglePause(true);
      setModalStateOpen(false);
      setCurrentDialogAction("DISCARD");
    } else {
      handleClose();
    }
  };

  const closeDialogueBox = () => {
    setModalStateOpen(true);
    setCurrentDialogAction("");
  };

  const handleSave = () => {
    setModalStateOpen(false);
    stopRecording();
  };

  // cancel recording
  useImperativeHandle(forwardedRef, () => ({
    cancelRecording: () => {
      cancelRecording();
    },
  }));

  const timerClass = classNames(classes.timer, "heading-3");
  const pausedTextClass = classNames(classes.pausedText, "text-label-2");

  return (
    <>
      {modalStateOpen && (
        <Modal width="440px" height="416px" onClose={handleCloseModal}>
          <Modal.Header
            title={
              headerLabel
                ? t(headerLabel)
                : t("common:recording_with_label", {
                    label: t("common:audio"),
                  })
            }
          />
          <Modal.Body>
            <div className={classes.container}>
              <div className={classes.progressContainer}>
                <RadialProgressBar
                  size={200}
                  value={calculatedPercent}
                  circleStroke={colors.surfaceActive}
                  progressStroke={colors.surfaceOnSuccess}
                  isArc={true}
                  circleStrokeWidth={4}
                />
                <div className={timerClass}>{timer}</div>
                {isPaused && (
                  <div className={pausedTextClass}>{t("common:paused")}</div>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className={classes.controls}>
              <div className={classes.buttonsContainer}>
                {playerState === "recording" && timeStamp > 0 ? (
                  <div className={classes.buttonCol}>
                    <Button
                      variant={"outlined"}
                      size={"large"}
                      onClick={() => togglePause(!isPaused)}
                      icon={isPaused ? <PlayFilled /> : <PauseFilled />}
                      isFullWidth
                    >
                      {isPaused ? t("common:resume") : t("common:pause")}
                    </Button>
                    <Button
                      variant={"progressive"}
                      size={"large"}
                      onClick={handleSave}
                      icon={<TickLargeOutlined variant={"on"} />}
                      isFullWidth
                    >
                      {t("common:done")}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant={"progressive"}
                    size={"large"}
                    onClick={startRecording}
                    icon={<MicrophoneOutlined variant={"on"} />}
                    isFullWidth
                  >
                    {t("label_with_recording", {
                      label: t("common:start"),
                    })}
                  </Button>
                )}
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      )}
      {!!currentDialogAction && (
        <Dialog
          variant="delete-dialog"
          title={t(DIALOGS_INFO[currentDialogAction].title, {
            label: t(DIALOGS_INFO[currentDialogAction].label),
          })}
          message={t(DIALOGS_INFO[currentDialogAction].message)}
          buttonOneText={t(DIALOGS_INFO[currentDialogAction].cancelButtonText)}
          buttonTwoText={t(DIALOGS_INFO[currentDialogAction].discardButtonText)}
          onButtonOneClick={closeDialogueBox}
          onButtonTwoClick={handleClose}
          isClosable={false}
        />
      )}
    </>
  );
});

AudioRecorderV3.displayName = "AudioRecorderV3";

const mapActionCreators = { setToastMsg, updateAudioRecorderBlob };
const mapStateToProps = state => {
  return {
    draftRecording: _.get(state, "app_services.draftRecording", null),
  };
};

const propTypes = {};
const defaultProps = {
  saveDraftRecording: true,
};

AudioRecorderV3.propTypes = propTypes;
AudioRecorderV3.defaultProps = defaultProps;

const ComposedAudioRecorder = compose(
  I18nHOC({ resource: "common" }),
  connect(mapStateToProps, mapActionCreators, null, { forwardRef: true }),
  withRecorder
)(AudioRecorderV3);

ComposedAudioRecorder.propTypes = propTypes;
ComposedAudioRecorder.defaultProps = defaultProps;

export default ComposedAudioRecorder;
